import React, {useEffect, useRef, useState, useMemo} from 'react';
import {PlusOutlined} from '@ant-design/icons';
import type {InputRef} from 'antd';
import {Flex, Input, Tag, theme, Tooltip} from 'antd';

const colors = [
    "magenta", "red", "volcano", "orange", "gold",
    "lime", "green", "cyan", "blue", "geekblue", "purple"
];

const getRandomColor = () => colors[Math.floor(Math.random() * colors.length)];

interface AddTagsProps {
    tagLabel:string;
    value?: string[];
    onChange?: (value: string[]) => void;
}

const AddTags: React.FC<AddTagsProps> = ({value = [], onChange,tagLabel}) => {
    const {token} = theme.useToken();
    const [inputVisible, setInputVisible] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [editIndex, setEditIndex] = useState<number | null>(null);
    const [editValue, setEditValue] = useState('');

    const inputRef = useRef<InputRef>(null);
    const editInputRef = useRef<InputRef>(null);

    // Persist random colors per tag so colors don't change when value changes
    const colorMap = useMemo(() => {
        const map: Record<string, string> = {};
        value.forEach(tag => {
            map[tag] = map[tag] || getRandomColor();
        });
        return map;
    }, [value]);

    const handleAdd = () => {
        if (inputValue.trim() && !value.includes(inputValue)) {
            onChange?.([...value, inputValue.trim()]);
        }
        setInputVisible(false);
        setInputValue('');
    };

    const handleRemove = (tag: string) => {
        onChange?.(value.filter(t => t !== tag));
    };

    const handleEditConfirm = () => {
        if (editIndex !== null && editValue.trim()) {
            const updated = [...value];
            updated[editIndex] = editValue.trim();
            onChange?.(updated);
        }
        setEditIndex(null);
        setEditValue('');
    };

    useEffect(() => {
        if (inputVisible) inputRef.current?.focus();
    }, [inputVisible]);

    useEffect(() => {
        if (editIndex !== null) editInputRef.current?.focus();
    }, [editIndex]);

    const tagPlusStyle: React.CSSProperties = {
        background: token.colorBgContainer,
        borderStyle: 'dashed',
        cursor: 'pointer',
        padding:'5px'
    };

    return (
        <Flex gap="4px 0" wrap>
            {value.map((tag, i) => {
                if (editIndex === i) {
                    return (
                        <Input
                            ref={editInputRef}
                            key={tag}
                            size="small"
                            value={editValue}
                            onChange={e => setEditValue(e.target.value)}
                            onBlur={handleEditConfirm}
                            onPressEnter={handleEditConfirm}
                            style={{width: 80}}
                        />
                    );
                }

                const isLongTag = tag.length > 20;
                const display = isLongTag ? `${tag.slice(0, 20)}...` : tag;

                const tagElem = (
                    <Tag
                        key={tag}
                        closable
                        color={colorMap[tag]}
                        onClose={() => handleRemove(tag)}
                    >
            <span
                onDoubleClick={() => {
                    setEditIndex(i);
                    setEditValue(tag);
                }}
            >
              {display}
            </span>
                    </Tag>
                );

                return isLongTag ? <Tooltip key={tag} title={tag}>{tagElem}</Tooltip> : tagElem;
            })}

            {inputVisible ? (
                <Input
                    ref={inputRef}
                    size="small"
                    value={inputValue}
                    onChange={e => setInputValue(e.target.value)}
                    onPressEnter={handleAdd}
                    onBlur={handleAdd}
                    style={{width: 80}}
                />
            ) : (
                <Tag style={tagPlusStyle} icon={<PlusOutlined/>} onClick={() => setInputVisible(true)}>
                    {tagLabel}
                </Tag>
            )}
        </Flex>
    );
};

export default AddTags;
